import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { trackConversion } from "@/lib/gtag";
import { submitBooking } from "@/lib/bookings.functions";
import { estimateFare, formatAud, parseWhen, type FareEstimate } from "@/lib/fare";
import {
  Car,
  Clock,
  Calendar,
  MapPin,
  Users,
  Luggage,
  Phone,
  Mail,
  ShieldCheck,
  HelpCircle,
  Receipt,
  CheckCircle2,
  MessageCircle,
  ChevronDown,
} from "lucide-react";

export type Coords = { lat: number; lon: number };

/** Sensible default: today's date and the next half hour, ~2 hours out. */
function defaultWhen() {
  const d = new Date(Date.now() + 2 * 60 * 60 * 1000);
  d.setMinutes(d.getMinutes() > 30 ? 60 : 30, 0, 0);
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

export function BookingPanel({
  heading = "BOOK A RIDE",
  subheading = "Quick, easy and secure booking",
  defaultPickup = "",
  defaultDropoff = "",
  conversionName = "booking",
}: {
  heading?: string;
  subheading?: string;
  defaultPickup?: string;
  defaultDropoff?: string;
  conversionName?: "booking" | "airportBooking";
}) {
  const submit = useServerFn(submitBooking);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [pickup, setPickup] = useState(defaultPickup);
  const [dropoff, setDropoff] = useState(defaultDropoff);
  const [pickupDate, setPickupDate] = useState(() => defaultWhen().date);
  const [pickupTime, setPickupTime] = useState(() => defaultWhen().time);
  const [tripType, setTripType] = useState<"One Way" | "Return">("One Way");
  const [vehicle, setVehicle] = useState("Premium Sedan");
  const [flight, setFlight] = useState("");
  const [notes, setNotes] = useState("");
  const [showExtras, setShowExtras] = useState(false);
  const [confirmed, setConfirmed] = useState<null | {
    name: string;
    pickup: string;
    dropoff: string;
    when: string;
    vehicle: string;
    tripType: string;
    fare: string | null;
  }>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pickupCoords, setPickupCoords] = useState<Coords | null>(null);
  const [dropoffCoords, setDropoffCoords] = useState<Coords | null>(null);
  const [route, setRoute] = useState<{ km: number; min: number } | null>(null);
  const [routing, setRouting] = useState(false);

  // Fetch the real road route whenever both endpoints are picked from suggestions.
  useEffect(() => {
    if (!pickupCoords || !dropoffCoords) {
      setRoute(null);
      return;
    }
    const ctrl = new AbortController();
    setRouting(true);
    const t = setTimeout(async () => {
      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${pickupCoords.lon},${pickupCoords.lat};${dropoffCoords.lon},${dropoffCoords.lat}?overview=false`;
        const res = await fetch(url, { signal: ctrl.signal });
        if (!res.ok) return;
        const data = (await res.json()) as {
          routes?: { distance: number; duration: number }[];
        };
        const r = data.routes?.[0];
        if (!r) return;
        setRoute({ km: r.distance / 1000, min: r.duration / 60 });
      } catch {
        // routing unavailable — estimate simply stays hidden
      } finally {
        setRouting(false);
      }
    }, 250);
    return () => {
      ctrl.abort();
      clearTimeout(t);
      setRouting(false);
    };
  }, [pickupCoords, dropoffCoords]);

  const fare: FareEstimate | null = route
    ? estimateFare({
        distanceKm: route.km,
        durationMin: route.min,
        when: parseWhen(pickupDate, pickupTime),
        vehicle,
        returnTrip: tripType === "Return",
      })
    : null;

  const vehicles = [
    { name: "Premium Sedan", pax: 4, bags: 3 },
    { name: "Premium SUV", pax: 6, bags: 5 },
    { name: "Maxi", pax: 11, bags: 9 },
  ];

  async function handleSubmit() {
    if (!name.trim() || !phone.trim() || !pickup.trim() || !dropoff.trim()) {
      toast.error("Please fill in your name, phone, pickup and drop-off.");
      return;
    }
    setSubmitting(true);
    try {
      await submit({
        data: {
          customer_name: name,
          customer_email: email,
          customer_phone: phone,
          pickup,
          dropoff,
          pickup_date: pickupDate,
          pickup_time: pickupTime,
          trip_type: tripType,
          vehicle,
          flight_number: flight,
          notes,
          estimated_fare: fare?.total,
          distance_km: fare ? Math.round(fare.distanceKm * 10) / 10 : undefined,
          duration_min: fare ? Math.round(fare.durationMin) : undefined,
        },
      });
      toast.success("Booking received! We'll confirm shortly.");
      setConfirmed({
        name,
        pickup,
        dropoff,
        when: `${pickupDate} at ${pickupTime}`,
        vehicle,
        tripType,
        fare: fare ? `${formatAud(fare.low)} – ${formatAud(fare.high)}` : null,
      });
      trackConversion(conversionName, {
        value: fare?.total,
        distanceKm: fare?.distanceKm,
        airport: conversionName === "airportBooking",
      });
      setName(""); setEmail(""); setPhone("");
      setPickup(""); setDropoff("");
      setFlight(""); setNotes("");
      setPickupCoords(null); setDropoffCoords(null); setRoute(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      {confirmed && (
        <div className="rounded-xl border border-primary/50 bg-card p-6">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-6 w-6 text-primary" />
            <div>
              <h2 className="text-lg font-bold tracking-wide">BOOKING REQUEST RECEIVED</h2>
              <p className="text-xs text-muted-foreground">
                Thanks {confirmed.name.split(" ")[0]} — our dispatch team will call or email
                you shortly to confirm your driver.
              </p>
            </div>
          </div>
          <dl className="mt-5 space-y-2 rounded-lg border border-border/60 bg-secondary/40 p-4 text-sm">
            {[
              ["Pickup", confirmed.pickup],
              ["Drop-off", confirmed.dropoff],
              ["When", confirmed.when],
              ["Vehicle", `${confirmed.vehicle} · ${confirmed.tripType}`],
              ...(confirmed.fare ? [["Estimated fare", confirmed.fare]] : []),
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="text-right font-semibold">{v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href="tel:0493100265"
              onClick={() => trackConversion("phoneCall")}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
            >
              <Phone className="h-4 w-4" />
              Call to confirm now
            </a>
            <button
              type="button"
              onClick={() => setConfirmed(null)}
              className="inline-flex items-center rounded-full border border-border px-5 py-2.5 text-sm font-semibold hover:border-primary hover:text-primary"
            >
              Make another booking
            </button>
          </div>
        </div>
      )}
      <div className="rounded-xl border border-border/60 bg-card p-6">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 text-primary">
            <Car className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold tracking-wide">{heading}</h2>
            <p className="text-xs text-muted-foreground">{subheading}</p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <a
            href="tel:0493100265"
            onClick={() => trackConversion("phoneCall")}
            className="flex min-h-[48px] items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-bold tracking-wide text-primary-foreground"
          >
            <Phone className="h-4 w-4" />
            CALL NOW
          </a>
          <a
            href="https://wa.me/61493100265?text=Hi%20SE%20Premium%20Cabs%2C%20I%27d%20like%20to%20book%20a%20ride"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackConversion("whatsapp")}
            className="flex min-h-[48px] items-center justify-center gap-2 rounded-md border border-primary px-4 text-sm font-bold tracking-wide text-primary"
          >
            <MessageCircle className="h-4 w-4" />
            WHATSAPP
          </a>
        </div>
        <p className="mt-2 text-center text-[11px] text-muted-foreground">
          Need a cab now? Call — we answer 24/7. Or book below in 30 seconds.
        </p>

        <div className="mt-6 space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="YOUR NAME">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                className="w-full rounded-md border border-border bg-input px-4 py-3 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none"
              />
            </Field>
            <Field label="PHONE">
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="04xx xxx xxx"
                className="w-full rounded-md border border-border bg-input px-4 py-3 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none"
              />
            </Field>
          </div>
          <Field label="PICKUP LOCATION">
            <AddressAutocomplete
              value={pickup}
              onChange={(v) => { setPickup(v); setPickupCoords(null); }}
              onSelect={(v, c) => { setPickup(v); setPickupCoords(c); }}
              placeholder="Enter pickup location"
            />
          </Field>
          <Field label="DROP-OFF LOCATION">
            <AddressAutocomplete
              value={dropoff}
              onChange={(v) => { setDropoff(v); setDropoffCoords(null); }}
              onSelect={(v, c) => { setDropoff(v); setDropoffCoords(c); }}
              placeholder="Enter drop-off location"
            />
          </Field>
          <div>
            <span className="text-[10px] font-bold tracking-widest text-muted-foreground">
              DATE &amp; TIME
            </span>
            <div className="mt-1.5 grid grid-cols-2 gap-3">
              <div className="relative">
                <input
                  type="date"
                  value={pickupDate}
                  aria-label="Pickup date"
                  onChange={(e) => setPickupDate(e.target.value)}
                  className="w-full rounded-md border border-border bg-input px-4 py-3 pr-10 text-sm focus:border-primary focus:outline-none"
                />
                <Calendar className="pointer-events-none absolute right-3 top-3 h-4 w-4 text-primary" />
              </div>
              <div className="relative">
                <input
                  type="time"
                  value={pickupTime}
                  aria-label="Pickup time"
                  onChange={(e) => setPickupTime(e.target.value)}
                  className="w-full rounded-md border border-border bg-input px-4 py-3 pr-10 text-sm focus:border-primary focus:outline-none"
                />
                <Clock className="pointer-events-none absolute right-3 top-3 h-4 w-4 text-primary" />
              </div>
            </div>
          </div>
          <div>
            <span className="text-[10px] font-bold tracking-widest text-muted-foreground">
              TRIP TYPE
            </span>
            <div role="group" aria-label="Trip type" className="mt-1.5 grid grid-cols-2 gap-0 rounded-md bg-input p-1">
              <button
                type="button"
                onClick={() => setTripType("One Way")}
                aria-pressed={tripType === "One Way"}
                className={`rounded-md py-2.5 text-sm font-semibold ${
                  tripType === "One Way"
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground"
                }`}
              >
                One Way
              </button>
              <button
                type="button"
                onClick={() => setTripType("Return")}
                aria-pressed={tripType === "Return"}
                className={`rounded-md py-2.5 text-sm font-semibold ${
                  tripType === "Return"
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground"
                }`}
              >
                Return
              </button>
            </div>
          </div>
          <div>
            <span className="text-[10px] font-bold tracking-widest text-muted-foreground">
              VEHICLE
            </span>
            <div role="group" aria-label="Vehicle type" className="mt-1.5 grid grid-cols-1 gap-2">
              {vehicles.map((v) => {
                const selected = vehicle === v.name;
                return (
                  <button
                    key={v.name}
                    type="button"
                    onClick={() => setVehicle(v.name)}
                    aria-pressed={selected}
                    className={`flex items-center gap-3 rounded-md border p-3 text-left transition-colors ${
                      selected
                        ? "border-primary bg-primary/10"
                        : "border-border bg-input hover:border-primary/50"
                    }`}
                  >
                    <div className="grid h-10 w-16 shrink-0 place-items-center rounded bg-secondary">
                      <Car className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{v.name}</div>
                      <div className="mt-0.5 flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" /> {v.pax}
                        </span>
                        <span className="flex items-center gap-1">
                          <Luggage className="h-3 w-3" /> {v.bags}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <button
              type="button"
              onClick={() => setShowExtras((s) => !s)}
              className="flex w-full items-center justify-between rounded-md border border-border bg-input px-4 py-3 text-xs font-semibold tracking-wide text-muted-foreground"
            >
              Email, flight number or special requests (optional)
              <ChevronDown
                className={`h-4 w-4 text-primary transition-transform ${showExtras ? "rotate-180" : ""}`}
              />
            </button>
            {showExtras && (
              <div className="mt-3 space-y-4">
                <Field label="EMAIL (OPTIONAL)">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full rounded-md border border-border bg-input px-4 py-3 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none"
                  />
                </Field>
                <Field label="FLIGHT NUMBER (for airport pickup)">
                  <input
                    type="text"
                    value={flight}
                    onChange={(e) => setFlight(e.target.value)}
                    placeholder="e.g. QF123"
                    className="w-full rounded-md border border-border bg-input px-4 py-3 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none"
                  />
                </Field>
                <Field label="SPECIAL REQUESTS">
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Child seat, extra luggage, etc."
                    className="w-full rounded-md border border-border bg-input px-4 py-3 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none"
                  />
                </Field>
              </div>
            )}
          </div>
          {(routing || fare) && (
            <div className="rounded-lg border border-primary/40 bg-primary/5 p-4">
              {!fare ? (
                <div className="text-xs text-muted-foreground">Calculating fare estimate…</div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-[10px] font-bold tracking-widest text-muted-foreground">
                    <Receipt className="h-3.5 w-3.5 text-primary" />
                    ESTIMATED FARE
                  </div>
                  <div className="mt-1 text-2xl font-bold text-primary">
                    {formatAud(fare.low)} – {formatAud(fare.high)}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {fare.distanceKm.toFixed(1)} km · approx {Math.round(fare.durationMin)} min ·{" "}
                    {fare.label}
                    {tripType === "Return" ? " · return trip" : ""}
                  </div>
                  <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
                    Estimate only, based on Victorian metered taxi rates. Tolls, airport fees,
                    waiting time and traffic can change the final fare.
                  </p>
                </>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="mt-2 w-full rounded-md bg-primary py-3.5 text-sm font-bold tracking-wider text-primary-foreground transition-transform hover:scale-[1.01] disabled:opacity-60"
          >
            {submitting ? "SENDING..." : "CONFIRM BOOKING"}
          </button>
          <p className="text-center text-[11px] text-muted-foreground">
            No payment needed now · Free cancellation · We confirm by call or text within minutes
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-border/60 bg-card p-6 space-y-5">
        <ContactRow icon={Phone} title="Call Us" lines={["0493 100 265", "Available 24/7"]} />
        <ContactRow icon={Mail} title="Email Us" lines={["sepremiumcabs@gmail.com", "We reply within minutes"]} />
        <ContactRow icon={ShieldCheck} title="Safe & Secure" lines={["Your safety is our top priority"]} />

        <div className="rounded-lg bg-primary p-5 text-primary-foreground">
          <div className="flex items-center gap-2 text-sm font-bold">
            <HelpCircle className="h-4 w-4" />
            NEED HELP?
          </div>
          <p className="mt-1 text-xs">We're here to help you anytime, anywhere.</p>
          <a
            href="tel:+61493100265"
            onClick={() => trackConversion("phoneCall")}
            className="mt-4 inline-block rounded-md bg-background px-5 py-2.5 text-xs font-bold tracking-wider text-foreground transition-colors hover:bg-secondary"
          >
            CONTACT US
          </a>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  // <label> wraps the control so the text is programmatically the field's
  // accessible name without needing generated ids.
  return (
    <label className="block">
      <span className="text-[10px] font-bold tracking-widest text-muted-foreground">
        {label}
      </span>
      <div className="relative mt-1.5">{children}</div>
    </label>
  );
}

type NominatimResult = {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
};

function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  onSelect: (v: string, coords: Coords) => void;
  placeholder: string;
}) {
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const skipNext = useRef(false);
  // Never look up the value the field was pre-filled with (e.g. airport page)
  const initialValue = useRef(value);

  useEffect(() => {
    if (skipNext.current) {
      skipNext.current = false;
      return;
    }
    const q = value.trim();
    if (q.length < 3 || q === initialValue.current.trim()) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&countrycodes=au&limit=5&q=${encodeURIComponent(q)}`;
        const res = await fetch(url, {
          signal: ctrl.signal,
          headers: { Accept: "application/json" },
        });
        if (!res.ok) return;
        const data = (await res.json()) as NominatimResult[];
        setSuggestions(data);
        setOpen(true);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => {
      ctrl.abort();
      clearTimeout(t);
    };
  }, [value]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={boxRef}>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        className="w-full rounded-md border border-border bg-input px-4 py-3 pr-10 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none"
      />
      <MapPin className="pointer-events-none absolute right-3 top-3 h-4 w-4 text-primary" />
      {open && suggestions.length > 0 && (
        <ul className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-auto rounded-md border border-border bg-card shadow-lg">
          {suggestions.map((s) => (
            <li key={s.place_id}>
              <button
                type="button"
                onClick={() => {
                  skipNext.current = true;
                  onSelect(s.display_name, { lat: Number(s.lat), lon: Number(s.lon) });
                  setOpen(false);
                }}
                className="block w-full px-4 py-2 text-left text-xs hover:bg-primary/10"
              >
                {s.display_name}
              </button>
            </li>
          ))}
        </ul>
      )}
      {loading && (
        <div className="absolute right-9 top-3 text-[10px] text-muted-foreground">…</div>
      )}
    </div>
  );
}

function ContactRow({
  icon: Icon,
  title,
  lines,
}: {
  icon: typeof Phone;
  title: string;
  lines: string[];
}) {
  return (
    <div className="flex items-start gap-4">
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-primary/40 text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-sm font-semibold">{title}</div>
        {lines.map((l, i) => (
          <div key={i} className={i === 0 ? "text-sm text-primary" : "text-xs text-muted-foreground"}>
            {l}
          </div>
        ))}
      </div>
    </div>
  );
}